import { KnownSurveys, type SurveyData, type SurveyPayload } from "@qendo/survey";
import type { ListData } from "@qendo/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AxiosResponse } from "axios";
import { createContext, type ReactNode, useContext, useMemo } from "react";
import { type ApiError, useApi } from "~/providers/ApiProvider";
import { useFeedback } from "~/providers/FeedbackProvider";
import { useSession } from "~/providers/SessionProvider";
import { useUser } from "~/providers/UserProvider";

declare interface SurveyState {
    surveys: SurveyData[];
    findSurvey(id: string): SurveyData | null;
    respond(payload: SurveyPayload): Promise<void>;
}

export const SurveyContext = createContext<SurveyState>({
    surveys: [],
    findSurvey: (_id) => null,
    respond: async (_payload) => {
        return;
    },
});

export function useSurvey() {
    const value = useContext(SurveyContext);
    if (!value) {
        throw new Error("useSurvey must be wrapped in a <SurveyProvider />");
    }
    return value;
}

export function SurveyProvider({ children }: { children: ReactNode }) {
    const { client } = useApi();
    const { addToast } = useFeedback();
    const { is_share_user } = useSession();
    const { user } = useUser();
    const queryClient = useQueryClient();

    const queryKey = ["surveys", user.id];

    const surveyQuery = useQuery({
        queryKey,
        queryFn: async () => {
            if (user.id && !is_share_user) {
                return await client()
                    .get("surveys")
                    .then((response: AxiosResponse<ListData<SurveyData>>) => {
                        return response.data.data;
                    });
            }

            return [];
        },
        placeholderData: [],
        staleTime: 1000 * 60 * 60 * 24, // 24 hours
        refetchOnMount: true,
    });

    const surveys = useMemo(
        () => surveyQuery.data?.filter((s) => KnownSurveys.includes(s.schema)) ?? [],
        [surveyQuery],
    );

    const storeMutation = useMutation({
        mutationFn: async ({ id, response }: SurveyPayload) => {
            return await client()
                .post(`surveys/${id}`, { response })
                .then(() => {
                    addToast({ content: "Survey response recorded." });

                    return id;
                })
                .catch((error: ApiError) => {
                    // convert a 403 into a valid "completed" response
                    if (error.status === 403) {
                        return id;
                    }

                    throw error;
                });
        },
        onSuccess: (id: string) => {
            queryClient.setQueryData(queryKey, (cache?: SurveyData[]) => {
                return (cache ?? surveys).filter((survey) => survey.id !== id);
            });
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey, refetchType: "all" });
        },
    });

    const findSurvey = (id: string) => surveys.find((survey) => survey.id === id) ?? null;

    const respond = async ({ id, response }: SurveyPayload) => {
        if (!user.id || !findSurvey(id) || is_share_user) {
            return;
        }

        await storeMutation.mutateAsync({ id, response });
    };

    const value = {
        surveys,
        findSurvey,
        respond,
    };

    return <SurveyContext.Provider value={value}>{children}</SurveyContext.Provider>;
}
